import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { money } from "../calc";
import { generateInvoicePDF } from "../pdf";

const STATUS_OPTS = [
  { v: "trial", l: "Em teste" },
  { v: "active", l: "Ativa" },
  { v: "past_due", l: "Inadimplente" },
  { v: "suspended", l: "Suspensa" },
  { v: "cancelled", l: "Cancelada" },
];
const statusLabel = (v) => (STATUS_OPTS.find((s) => s.v === v)?.l || v);

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rs-modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close">×</button></div>
        <div className="rs-modal-body">{children}</div>
        {footer && <div className="rs-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [logs, setLogs] = useState([]);
  const [mrr, setMrr] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invMonth, setInvMonth] = useState(new Date().toISOString().slice(0, 7));
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nomeFantasia: "", razaoSocial: "", cnpj: "", email: "", telefone: "", adminName: "", adminEmail: "", password: "", trialDays: 14 });
  const emptyClinic = { id: null, name: "", address: "", cep: "", city: "", uf: "", phone: "" };
  const [clinics, setClinics] = useState([]);
  const [clinicForm, setClinicForm] = useState(emptyClinic);
  const [clinicQ, setClinicQ] = useState("");
  const loadClinics = async () => {
    try { const { data } = await api.get(`/admin/clinics${clinicQ ? `?q=${encodeURIComponent(clinicQ)}` : ""}`); setClinics(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sfc = (k) => (e) => setClinicForm({ ...clinicForm, [k]: e.target.value });
  const saveClinic = async () => {
    if (!clinicForm.name.trim() || !clinicForm.address.trim()) return toast.error("Informe nome e endereço da clínica.");
    try {
      if (clinicForm.id) await api.patch(`/admin/clinics/${clinicForm.id}`, clinicForm);
      else await api.post("/admin/clinics", clinicForm);
      toast.success("Clínica salva."); setClinicForm(emptyClinic); loadClinics();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteClinic = async (c) => {
    if (!window.confirm(`Excluir a clínica "${c.name}" da base global?`)) return;
    try { await api.delete(`/admin/clinics/${c.id}`); toast.success("Clínica excluída."); loadClinics(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    try {
      const [d, c, p] = await Promise.all([
        api.get("/admin/dashboard"), api.get("/admin/companies"), api.get("/admin/plans"),
      ]);
      setDash(d.data); setCompanies(c.data); setPlans(p.data);
      api.get("/admin/mrr").then((r) => setMrr(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLogs = async () => {
    try { const { data } = await api.get("/admin/logs"); setLogs(data); } catch (e) { console.error("Falha ao carregar logs:", e); }
  };

  const loadInvoices = (m) => api.get(`/admin/invoices${m ? `?month=${m}` : ""}`).then((r) => setInvoices(r.data)).catch(() => {});

  const changeStatus = async (id, status) => {
    try { await api.patch(`/admin/companies/${id}`, { status }); toast.success("Status atualizado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const grantDays = async (id) => {
    const days = prompt("Conceder quantos dias grátis?", "7");
    if (!days) return;
    try { await api.patch(`/admin/companies/${id}`, { grantTrialDays: Number(days) }); toast.success("Dias concedidos."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/companies/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const notifyCompany = async (id) => {
    try { await api.post(`/admin/companies/${id}/notify`); toast.success("Cobrança/aviso enviado ao cliente por e-mail."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const notifyAllOverdue = async () => {
    if (!window.confirm("Enviar aviso de cobrança a TODAS as empresas em atraso/suspensas?")) return;
    try { const { data } = await api.post("/admin/notify-overdue"); toast.success(`Aviso enviado para ${data.count} empresa(s).`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const daysOverdue = (c) => {
    const since = c.pastDueSince || c.updatedAt;
    if (!since) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 86400000));
  };

  const createCompany = async () => {
    try {
      await api.post("/admin/companies", form);
      toast.success("Empresa criada.");
      setShowCreate(false); setForm({ nomeFantasia: "", razaoSocial: "", cnpj: "", email: "", telefone: "", adminName: "", adminEmail: "", password: "", trialDays: 14 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updatePlan = async (p, patch) => {
    try { await api.patch(`/admin/plans/${p.id}`, patch); toast.success("Plano atualizado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Excluir a empresa "${c.nomeFantasia || c.email}"? Esta ação é permanente e apaga TODOS os dados (usuários, pacientes, orçamentos, remoções, veículos, financeiro e assinatura) desta empresa.`)) return;
    try { await api.delete(`/admin/companies/${c.id}`); toast.success("Empresa excluída."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sf = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="admin-shell">
      <div className="admin-top">
        <div className="brand">
          <div className="logo-chip"><img src="/rotasaude-logo.png" alt="RotaSaúde" /></div>
          <div><strong>Administração RotaSaúde</strong><small>Painel do proprietário da plataforma</small></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 13, color: "#bcd6ec" }}>{user?.email}</span>
          <button onClick={logout} data-testid="admin-logout" style={{ background: "rgba(255,255,255,.12)", color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontWeight: 800, cursor: "pointer" }}>Sair</button>
        </div>
      </div>

      <div className="admin-main">
        <div className="admin-tabs">
          <button className={tab === "dashboard" ? "active" : ""} onClick={() => setTab("dashboard")} data-testid="admin-tab-dashboard">Dashboard</button>
          <button className={tab === "empresas" ? "active" : ""} onClick={() => setTab("empresas")} data-testid="admin-tab-empresas">Empresas</button>
          <button className={tab === "inadimplencia" ? "active" : ""} onClick={() => setTab("inadimplencia")} data-testid="admin-tab-inadimplencia">Inadimplência</button>
          <button className={tab === "planos" ? "active" : ""} onClick={() => setTab("planos")} data-testid="admin-tab-planos">Planos</button>
          <button className={tab === "recibos" ? "active" : ""} onClick={() => { setTab("recibos"); loadInvoices(invMonth); }} data-testid="admin-tab-recibos">Recibos</button>
          <button className={tab === "logs" ? "active" : ""} onClick={() => { setTab("logs"); loadLogs(); }} data-testid="admin-tab-logs">Logs</button>
          <button className={tab === "clinicas" ? "active" : ""} onClick={() => { setTab("clinicas"); loadClinics(); }} data-testid="admin-tab-clinicas">Clínicas</button>
        </div>

        {tab === "dashboard" && dash && (
          <>
            <div className="admin-kpis">
              <div className="admin-kpi"><small>Empresas cadastradas</small><strong data-testid="kpi-total">{dash.totalCompanies}</strong></div>
              <div className="admin-kpi"><small>Ativas</small><strong>{dash.active}</strong></div>
              <div className="admin-kpi"><small>Em teste</small><strong>{dash.trial}</strong></div>
              <div className="admin-kpi accent"><small>MRR (assinaturas ativas)</small><strong>{money(dash.mrr)}</strong></div>
            </div>
            <div className="admin-kpis">
              <div className="admin-kpi"><small>Inadimplentes</small><strong>{dash.pastDue}</strong></div>
              <div className="admin-kpi"><small>Suspensas</small><strong>{dash.suspended}</strong></div>
              <div className="admin-kpi"><small>Canceladas</small><strong>{dash.cancelled}</strong></div>
              <div className="admin-kpi"><small>Usuários ativos</small><strong>{dash.activeUsers}</strong></div>
            </div>
            <div className="admin-section">
              <h2>Últimos acessos</h2>
              <table className="admin-table">
                <thead><tr><th>Usuário</th><th>E-mail</th><th>Último acesso</th></tr></thead>
                <tbody>
                  {dash.recentLogins.length === 0 && <tr><td colSpan={3} className="muted-note">Nenhum acesso registrado ainda.</td></tr>}
                  {dash.recentLogins.map((r, i) => (
                    <tr key={r.email || i}><td>{r.name}</td><td>{r.email}</td><td>{r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString("pt-BR") : "—"}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="admin-section">
              <h2>Receita recorrente (MRR) — últimos 6 meses</h2>
              {!mrr ? <p className="muted-note">Carregando…</p> : (
                <>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 18, height: 180, padding: "10px 6px 0", borderBottom: "2px solid #e3ebf1" }}>
                    {mrr.months.map((mo) => {
                      const max = Math.max(...mrr.months.map((x) => x.revenue), 1);
                      const h = Math.round((mo.revenue / max) * 150);
                      return (
                        <div key={mo.month} style={{ flex: 1, textAlign: "center" }} data-testid={`mrr-bar-${mo.month}`}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#0b477d", marginBottom: 4 }}>{money(mo.revenue)}</div>
                          <div style={{ height: h, background: "linear-gradient(180deg,#0a77c2,#0a477e)", borderRadius: "6px 6px 0 0", minHeight: 3 }} title={`${mo.month}: ${money(mo.revenue)}`} />
                          {mo.cancellations > 0 && <div style={{ fontSize: 10, color: "#b53b3b", marginTop: 3 }}>-{mo.cancellations} canc.</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {mrr.months.map((mo) => <div key={mo.month} style={{ flex: 1, textAlign: "center", fontSize: 10, color: "#6a7f92" }}>{mo.month.slice(5)}/{mo.month.slice(2, 4)}</div>)}
                  </div>
                  <p className="muted-note" style={{ marginTop: 12 }}>MRR atual: <strong style={{ color: "#0b477d" }}>{money(mrr.mrr)}</strong> • Assinaturas ativas: <strong>{mrr.activeSubs}</strong></p>
                </>
              )}
            </div>
          </>
        )}

        {tab === "recibos" && (
          <div className="admin-section">
            <h2>Histórico global de recibos
              <input type="month" className="mini-select" value={invMonth} onChange={(e) => { setInvMonth(e.target.value); loadInvoices(e.target.value); }} data-testid="admin-inv-month" />
            </h2>
            <table className="admin-table">
              <thead><tr><th>Empresa</th><th>Recibo</th><th>Valor</th><th>Pago em</th><th>Ação</th></tr></thead>
              <tbody>
                {invoices.length === 0 && <tr><td colSpan={5} className="muted-note">Nenhum recibo no período selecionado.</td></tr>}
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`admin-invoice-${inv.id}`}>
                    <td><strong>{inv.companyName}</strong></td>
                    <td>{(inv.providerPaymentId || inv.id || "").toString().slice(-8).toUpperCase()}</td>
                    <td>{money(inv.amount)}</td>
                    <td>{inv.paidAt ? new Date(inv.paidAt).toLocaleDateString("pt-BR") : "—"}</td>
                    <td><button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "5px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => generateInvoicePDF(inv, { name: inv.companyName })} data-testid={`admin-inv-pdf-${inv.id}`}>Baixar PDF</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "empresas" && (
          <div className="admin-section">
            <h2>Empresas <button className="tb-btn" style={{ background: "#0a477e", color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontWeight: 800, cursor: "pointer" }} onClick={() => setShowCreate(true)} data-testid="admin-create-company">+ Criar empresa</button></h2>
            <table className="admin-table">
              <thead><tr><th>Empresa</th><th>E-mail</th><th>Usuários</th><th>Vencimento teste</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {companies.length === 0 && <tr><td colSpan={6} className="muted-note">Nenhuma empresa cadastrada.</td></tr>}
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td><strong>{c.nomeFantasia || "—"}</strong></td>
                    <td>{c.email}</td>
                    <td>{c.userCount}</td>
                    <td>{c.trialEndsAt ? new Date(c.trialEndsAt).toLocaleDateString("pt-BR") : "—"}</td>
                    <td>
                      <select className="mini-select" value={c.status} onChange={(e) => changeStatus(c.id, e.target.value)} data-testid={`company-status-${c.id}`}>
                        {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                    </td>
                    <td>
                      <div className="inline-btns">
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => openDetail(c.id)} data-testid={`company-view-${c.id}`}>Abrir</button>
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => grantDays(c.id)}>Dias grátis</button>
                        <button className="mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer", background: "#fbeaea", color: "#b3261e" }} onClick={() => deleteCompany(c)} data-testid={`company-delete-${c.id}`}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "inadimplencia" && (
          <div className="admin-section">
            <h2>Inadimplência — empresas em atraso e suspensas
              {companies.filter((c) => ["past_due", "suspended"].includes(c.status)).length > 0 && (
                <button className="tb-btn" style={{ background: "#b53b3b", color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontWeight: 800, cursor: "pointer" }} onClick={notifyAllOverdue} data-testid="admin-notify-all">Cobrar todas</button>
              )}
            </h2>
            <table className="admin-table">
              <thead><tr><th>Empresa</th><th>E-mail</th><th>Status</th><th>Dias em atraso</th><th>Ações</th></tr></thead>
              <tbody>
                {companies.filter((c) => ["past_due", "suspended"].includes(c.status)).length === 0 && <tr><td colSpan={5} className="muted-note">Nenhuma empresa inadimplente ou suspensa. 🎉</td></tr>}
                {companies.filter((c) => ["past_due", "suspended"].includes(c.status)).map((c) => (
                  <tr key={c.id} data-testid={`overdue-${c.id}`}>
                    <td><strong>{c.nomeFantasia || "—"}</strong></td>
                    <td>{c.email}</td>
                    <td><span className="admin-status-chip" style={{ background: c.status === "suspended" ? "#fdecec" : "#fff2df", color: c.status === "suspended" ? "#a32a2a" : "#8a5a00" }}>{statusLabel(c.status)}</span></td>
                    <td>{daysOverdue(c)} dia(s)</td>
                    <td>
                      <div className="inline-btns">
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => notifyCompany(c.id)} data-testid={`notify-${c.id}`}>Cobrar</button>
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => changeStatus(c.id, "active")}>Reativar</button>
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => openDetail(c.id)}>Abrir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "planos" && (          <div className="admin-section">
            <h2>Planos</h2>
            {plans.map((p) => (
              <div key={p.id} className="flex-between" style={{ borderBottom: "1px solid #eef2f6", padding: "14px 0" }}>
                <div>
                  <strong style={{ color: "#0b477d", fontSize: 16 }}>{p.name}</strong>
                  <div className="muted-note">{p.maxVehicles ? `${p.maxVehicles} veículo(s)` : "Veículos ilimitados"} • {p.maxRemovals ? `${p.maxRemovals} remoções/mês` : "Remoções ilimitadas"} • {p.maxUsers} usuários • teste {p.trialDays} dias</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#435a71" }}>Mensalidade R$
                    <input type="number" step="0.01" defaultValue={p.price} style={{ width: 100, marginLeft: 8 }}
                      onBlur={(e) => Number(e.target.value) !== p.price && updatePlan(p, { price: Number(e.target.value) })} data-testid={`plan-price-${p.id}`} />
                  </label>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#435a71" }}>Veículos
                    <input type="number" defaultValue={p.maxVehicles ?? ""} placeholder="∞" style={{ width: 64, marginLeft: 8 }}
                      onBlur={(e) => String(e.target.value) !== String(p.maxVehicles ?? "") && updatePlan(p, { maxVehicles: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`plan-maxvehicles-${p.id}`} />
                  </label>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#435a71" }}>Remoções/mês
                    <input type="number" defaultValue={p.maxRemovals ?? ""} placeholder="∞" style={{ width: 64, marginLeft: 8 }}
                      onBlur={(e) => String(e.target.value) !== String(p.maxRemovals ?? "") && updatePlan(p, { maxRemovals: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`plan-maxremovals-${p.id}`} />
                  </label>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#435a71" }}>Usuários
                    <input type="number" defaultValue={p.maxUsers} style={{ width: 64, marginLeft: 8 }}
                      onBlur={(e) => Number(e.target.value) !== p.maxUsers && updatePlan(p, { maxUsers: Number(e.target.value) })} />
                  </label>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#435a71" }}>Teste (dias)
                    <input type="number" defaultValue={p.trialDays} style={{ width: 64, marginLeft: 8 }}
                      onBlur={(e) => Number(e.target.value) !== p.trialDays && updatePlan(p, { trialDays: Number(e.target.value) })} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "clinicas" && (
          <div className="admin-section">
            <h2>Clínicas — base global</h2>
            <p className="muted-note">Cadastro compartilhado entre todas as empresas. Aparece no autocomplete do "Novo Orçamento". Empresas podem adicionar; edição/exclusão só aqui pelo Super Admin.</p>
            <div style={{ display: "flex", gap: 8, margin: "12px 0", flexWrap: "wrap" }}>
              <input placeholder="Buscar por nome, cidade ou endereço" value={clinicQ} onChange={(e) => setClinicQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadClinics()} style={{ flex: 1, minWidth: 240 }} data-testid="clinic-search" />
              <button className="tb-btn" onClick={loadClinics} style={{ background: "#0a477e", color: "#fff", border: 0, borderRadius: 8, padding: "8px 16px", fontWeight: 800, cursor: "pointer" }} data-testid="clinic-search-btn">Buscar</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr .9fr 1fr .5fr 1fr auto", gap: 8, alignItems: "center", marginBottom: 14 }}>
              <input placeholder="Nome*" value={clinicForm.name} onChange={sfc("name")} data-testid="clinic-name" />
              <input placeholder="Endereço completo*" value={clinicForm.address} onChange={sfc("address")} data-testid="clinic-address" />
              <input placeholder="CEP" value={clinicForm.cep} onChange={sfc("cep")} data-testid="clinic-cep" />
              <input placeholder="Cidade" value={clinicForm.city} onChange={sfc("city")} data-testid="clinic-city" />
              <input placeholder="UF" maxLength={2} value={clinicForm.uf} onChange={sfc("uf")} data-testid="clinic-uf" />
              <input placeholder="Telefone" value={clinicForm.phone} onChange={sfc("phone")} data-testid="clinic-phone" />
              <div style={{ display: "flex", gap: 6 }}>
                <button className="tb-btn" onClick={saveClinic} style={{ background: "#20b35a", color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontWeight: 800, cursor: "pointer" }} data-testid="clinic-save">{clinicForm.id ? "Salvar" : "Adicionar"}</button>
                {clinicForm.id && <button onClick={() => setClinicForm(emptyClinic)} style={{ border: 0, borderRadius: 8, padding: "8px 12px", fontWeight: 800, cursor: "pointer" }}>Cancelar</button>}
              </div>
            </div>
            <table className="admin-table">
              <thead><tr><th>Nome</th><th>Endereço</th><th>Cidade/UF</th><th>Telefone</th><th>Ações</th></tr></thead>
              <tbody>
                {clinics.length === 0 && <tr><td colSpan={5} className="muted-note">Nenhuma clínica encontrada. Use a busca ou adicione uma acima.</td></tr>}
                {clinics.map((c) => (
                  <tr key={c.id} data-testid={`clinic-row-${c.id}`}>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.address}</td>
                    <td>{c.city || "—"}{c.uf ? `/${c.uf}` : ""}</td>
                    <td>{c.phone || "—"}</td>
                    <td>
                      <div className="inline-btns">
                        <button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => setClinicForm({ id: c.id, name: c.name, address: c.address, cep: c.cep || "", city: c.city || "", uf: c.uf || "", phone: c.phone || "" })} data-testid={`clinic-edit-${c.id}`}>Editar</button>
                        <button className="mini-btn" style={{ border: 0, borderRadius: 7, padding: "6px 10px", fontWeight: 800, cursor: "pointer", background: "#fbeaea", color: "#b3261e" }} onClick={() => deleteClinic(c)} data-testid={`clinic-delete-${c.id}`}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "logs" && (
          <div className="admin-section">
            <h2>Logs de auditoria (todas as empresas)</h2>
            <table className="admin-table">
              <thead><tr><th>Data</th><th>Empresa</th><th>Usuário</th><th>Ação</th><th>Entidade</th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={5} className="muted-note">Sem registros.</td></tr>}
                {logs.map((l) => (
                  <tr key={l.id}><td>{l.created_at ? new Date(l.created_at).toLocaleString("pt-BR") : "—"}</td><td>{l.tenant_id || "—"}</td><td>{l.user_name || "—"}</td><td>{l.action}</td><td>{l.entity}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <Modal title="Criar empresa" onClose={() => setShowCreate(false)}
          footer={<><button className="secondary" onClick={() => setShowCreate(false)} style={{ border: 0, borderRadius: 8, padding: "10px 16px", fontWeight: 800, cursor: "pointer" }}>Cancelar</button><button className="primary" onClick={createCompany} style={{ border: 0, borderRadius: 8, padding: "10px 16px", fontWeight: 800, cursor: "pointer" }} data-testid="admin-save-company">Criar empresa</button></>}>
          <div className="form-grid">
            <label>Nome fantasia<input value={form.nomeFantasia} onChange={sf("nomeFantasia")} data-testid="cc-nome" /></label>
            <label>Razão social<input value={form.razaoSocial} onChange={sf("razaoSocial")} /></label>
            <label>CNPJ<input value={form.cnpj} onChange={sf("cnpj")} /></label>
            <label>E-mail da empresa<input value={form.email} onChange={sf("email")} /></label>
            <label>Telefone<input value={form.telefone} onChange={sf("telefone")} /></label>
            <label>Dias de teste<input type="number" value={form.trialDays} onChange={sf("trialDays")} /></label>
            <label>Nome do administrador<input value={form.adminName} onChange={sf("adminName")} data-testid="cc-admin-name" /></label>
            <label>E-mail do administrador (login)<input value={form.adminEmail} onChange={sf("adminEmail")} data-testid="cc-admin-email" /></label>
            <label>Senha inicial<input value={form.password} onChange={sf("password")} data-testid="cc-password" placeholder="Padrão: rotasaude123" /></label>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={detail.company.nomeFantasia || "Empresa"} onClose={() => setDetail(null)}>
          <p className="muted-note">Status: <strong>{statusLabel(detail.company.status)}</strong> • Plano: {detail.plan?.name || "—"} ({money(detail.plan?.price || 0)}/mês)</p>
          <div className="admin-kpi-grid-6">
            <div className="admin-kpi"><small>Pacientes</small><strong>{detail.counts.patients}</strong></div>
            <div className="admin-kpi"><small>Orçamentos</small><strong>{detail.counts.quotes}</strong></div>
            <div className="admin-kpi"><small>Remoções</small><strong>{detail.counts.transports}</strong></div>
          </div>
          <h4 style={{ color: "#0b477d", marginTop: 18 }}>Usuários</h4>
          <table className="admin-table">
            <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Último acesso</th></tr></thead>
            <tbody>
              {detail.users.map((u) => (
                <tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td>{u.role}</td><td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("pt-BR") : "—"}</td></tr>
              ))}
            </tbody>
          </table>
          <h4 style={{ color: "#0b477d", marginTop: 18 }}>Recibos de mensalidade</h4>
          {(detail.invoices || []).length === 0 ? (
            <p className="muted-note">Nenhuma mensalidade paga registrada.</p>
          ) : (
            <table className="admin-table">
              <thead><tr><th>Recibo</th><th>Valor</th><th>Pago em</th><th>Ação</th></tr></thead>
              <tbody>
                {detail.invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{(inv.providerPaymentId || inv.id || "").toString().slice(-8).toUpperCase()}</td>
                    <td>{money(inv.amount)}</td>
                    <td>{inv.paidAt ? new Date(inv.paidAt).toLocaleDateString("pt-BR") : "—"}</td>
                    <td><button className="secondary mini-btn" style={{ border: 0, borderRadius: 7, padding: "5px 10px", fontWeight: 800, cursor: "pointer" }} onClick={() => generateInvoicePDF(inv, { name: detail.company.nomeFantasia })} data-testid={`admin-invoice-pdf-${inv.id}`}>Baixar PDF</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
}
